import { useState, type FormEvent, type ChangeEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Alert, AuthSurface, Button, Card, FormField, Input } from '@sakhtar/design-system/react';
import logoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import { createPublicCheckout } from '../../lib/publicApi.js';
import { IRAN_MOBILE_PATTERN } from '../../lib/phoneNumber.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: none. Confirmed 2026-09-20 (design-agent re-audit) by rendering
 *   every file in `docs/design-system/reference/` and reading it in full —
 *   no frame anywhere in the bundle shows a direct-checkout/pricing-
 *   calculator/payment flow. `Sakhtar Pilot P0.html`'s only subscription-
 *   related frame ("2.3 SUBSCRIPTION · 1000px") is explicitly captioned
 *   "PROVIDER-SAFE — بدون نام درگاه" (provider-safe, no gateway name) in
 *   the design's own review notes, and shows only plan/seat/trial status
 *   plus "انتخاب طرح" (choose plan) / "تماس با فروش" (contact sales)
 *   buttons — no calculator, no per-interval discount grid, no gateway
 *   branding. This page instead names the gateway directly in end-user
 *   copy ("درگاه پرداخت امن شاپرک / زرین‌پال") — the opposite of the one
 *   adjacent design principle that does exist for this surface, not merely
 *   a missing citation. This is `PR #166`'s own zero-citation build,
 *   already flagged in `docs/design-system/UI_REFERENCE.md`'s opening
 *   paragraph; recorded in full in
 *   `docs/design-system/PRODUCTION_READINESS_AUDIT.md`.
 * status: no design authority — do not treat this page's current visuals
 *   (raw hex colors, hand-rolled cards/badges instead of `V2Card`/
 *   `V2Badge`) as a pattern to copy elsewhere. Needs a real PO/Design Lead
 *   decision (a designed board, or an explicit "this surface is
 *   provider-facing and exempt" ruling) before further visual work here —
 *   not fabricated by this pass.
 */
type PricingInterval = '1_month' | '3_months' | '6_months' | '12_months';

interface IntervalOption {
  readonly id: PricingInterval;
  readonly label: string;
  readonly months: number;
  readonly discountPercent: number;
  readonly badge?: string;
}

const INTERVALS: readonly IntervalOption[] = [
  { id: '1_month', label: '۱ ماهه', months: 1, discountPercent: 0 },
  { id: '3_months', label: '۳ ماهه', months: 3, discountPercent: 5, badge: '۵٪ تخفیف' },
  { id: '6_months', label: '۶ ماهه', months: 6, discountPercent: 10, badge: '۱۰٪ تخفیف' },
  {
    id: '12_months',
    label: '۱ ساله',
    months: 12,
    discountPercent: 16.67,
    badge: 'به‌صرفه‌ترین (۱۶.۷٪ تخفیف)',
  },
];

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [usersCount, setUsersCount] = useState<number>(10);
  const [selectedInterval, setSelectedInterval] = useState<PricingInterval>('12_months');

  const [organizationName, setOrganizationName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [ownerMobile, setOwnerMobile] = useState('');
  const [password, setPassword] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusParam = searchParams.get('status');
  const reasonParam = searchParams.get('reason');

  // Exact Calculation Formula
  const baseUsers = 10;
  const extraUsers = Math.max(0, usersCount - baseUsers);
  const monthlyBaseToman = 12_000_000;
  const monthlyExtraToman = extraUsers * 500_000;
  const monthlyTotalToman = monthlyBaseToman + monthlyExtraToman;

  const currentIntervalConfig = INTERVALS.find((i) => i.id === selectedInterval) ?? INTERVALS[3]!;
  const rawTotalToman = monthlyTotalToman * currentIntervalConfig.months;

  let finalTotalToman: number;
  let discountToman: number;

  if (selectedInterval === '12_months') {
    // 2 months free = 10 months billed
    finalTotalToman = monthlyTotalToman * 10;
    discountToman = rawTotalToman - finalTotalToman;
  } else if (currentIntervalConfig.discountPercent > 0) {
    discountToman = Math.round((rawTotalToman * currentIntervalConfig.discountPercent) / 100);
    finalTotalToman = rawTotalToman - discountToman;
  } else {
    discountToman = 0;
    finalTotalToman = rawTotalToman;
  }

  const effectiveMonthlyToman = Math.round(finalTotalToman / currentIntervalConfig.months);

  function handleCompanyNameChange(name: string) {
    setOrganizationName(name);
    if (!slug) {
      const generated = name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 30);
      if (generated.length >= 3) {
        setSlug(generated);
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!organizationName.trim() || !slug.trim()) {
      setError('نام شرکت و شناسه آدرس الزامی است.');
      return;
    }
    if (!ownerMobile.trim().match(/^09\d{9}$/)) {
      setError('شماره موبایل مدیر باید ۱۱ رقم با فرمت 09xxxxxxxxx باشد.');
      return;
    }
    if (!ownerFirstName.trim() || !ownerLastName.trim()) {
      setError('نام و نام خانوادگی مدیر الزامی است.');
      return;
    }
    if (password.length < 8) {
      setError('رمز عبور باید حداقل ۸ کاراکتر باشد.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const idempotencyKey = `chk_${crypto.randomUUID()}`;
      const res = await createPublicCheckout(
        {
          usersCount,
          interval: selectedInterval,
          organizationName: organizationName.trim(),
          slug: slug.trim().toLowerCase(),
          ownerLoginIdentifier: ownerMobile.trim(),
          ownerFirstName: ownerFirstName.trim(),
          ownerLastName: ownerLastName.trim(),
          ownerPassword: password,
        },
        idempotencyKey,
      );

      if (res.paymentUrl) {
        window.location.href = res.paymentUrl;
      } else {
        setError('خطا در ایجاد تراکنش درگاه زرین‌پال.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطای برقراری ارتباط با سرور.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthSurface
      logo={<img src={logoUrl} alt="ساختار سیستم" height={32} />}
      brandLabel="ساختار سیستم"
      title="خرید مستقیم و راه‌اندازی آنی ERP ابری"
      subtitle="محاسبه قیمت بر اساس تعداد کاربران و دوره پرداخت • تحویل آنی پس از پرداخت"
    >
      <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'grid', gap: 16 }}>
        {statusParam === 'failed' ? (
          <Alert tone="danger" title="پرداخت انجام نشد">
            {reasonParam === 'cancelled'
              ? 'تراکنش توسط کاربر لغو شد یا درگاه بانکی پاسخ ناموفق ارسال کرد.'
              : 'اعتبارسنجی تراکنش ناموفق بود. در صورت کسر وجه از حساب، ظرف ۷۲ ساعت توسط بانک برگشت داده می‌شود.'}
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="danger" title="خطا">
            {error}
          </Alert>
        ) : null}

        {/* 1. Calculator Card */}
        <Card title="۱. ماشین حساب هزینه و انتخاب دوره">
          <div style={{ display: 'grid', gap: 14 }}>
            {/* User counter */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                }}
              >
                تعداد کاربران هم‌زمان:{' '}
                <span style={{ color: 'var(--brand-neon, #38bdf8)' }}>
                  {usersCount.toLocaleString('fa-IR')} کاربر
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setUsersCount((prev) => Math.max(1, prev - 1))}
                  style={{ minWidth: 42, fontSize: '1.2rem', fontWeight: 'bold' }}
                >
                  −
                </Button>
                <div
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    background: 'rgba(15, 23, 42, 0.6)',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid #334155',
                  }}
                >
                  <span style={{ fontSize: '1.15rem', fontWeight: 'bold' }}>
                    {usersCount.toLocaleString('fa-IR')}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', marginRight: 4 }}>
                    کاربر
                  </span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setUsersCount((prev) => prev + 1)}
                  style={{ minWidth: 42, fontSize: '1.2rem', fontWeight: 'bold' }}
                >
                  +
                </Button>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>
                {usersCount <= 10
                  ? 'تا ۱۰ کاربر در پلن پایه پوشش داده می‌شود (۱۲ میلیون تومان در ماه).'
                  : `۱۰ کاربر پایه + ${extraUsers.toLocaleString('fa-IR')} کاربر اضافه (${(extraUsers * 500_000).toLocaleString('fa-IR')} تومان ماهانه)`}
              </div>
            </div>

            {/* Intervals Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {INTERVALS.map((opt) => {
                const isSelected = opt.id === selectedInterval;
                return (
                  <div
                    key={opt.id}
                    onClick={() => setSelectedInterval(opt.id)}
                    style={{
                      border: isSelected
                        ? '2px solid var(--brand-neon, #38bdf8)'
                        : '1px solid #334155',
                      background: isSelected ? 'rgba(56, 189, 248, 0.12)' : 'rgba(15, 23, 42, 0.5)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}
                  >
                    {opt.badge ? (
                      <span
                        style={{
                          position: 'absolute',
                          top: -8,
                          left: 8,
                          background: opt.id === '12_months' ? '#10b981' : '#0284c7',
                          color: '#fff',
                          fontSize: '0.65rem',
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontWeight: 'bold',
                        }}
                      >
                        {opt.badge}
                      </span>
                    ) : null}
                    <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 2 }}>
                      {opt.discountPercent > 0
                        ? `${opt.discountPercent}٪ تخفیف دوره`
                        : 'بدون تخفیف'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Price Summary Breakdown */}
            <div
              style={{
                background: 'rgba(56, 189, 248, 0.05)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: 8,
                padding: 12,
                display: 'grid',
                gap: 6,
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}
              >
                <span style={{ color: '#94a3b8' }}>معادل هزینه ماهانه:</span>
                <span style={{ fontWeight: 'bold' }}>
                  {effectiveMonthlyToman.toLocaleString('fa-IR')} تومان
                </span>
              </div>
              {discountToman > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    color: '#10b981',
                  }}
                >
                  <span>سود شما از تخفیف دوره:</span>
                  <span>{discountToman.toLocaleString('fa-IR')} تومان</span>
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px dashed #334155',
                  paddingTop: 6,
                  marginTop: 2,
                  fontSize: '1.05rem',
                  fontWeight: 'bold',
                }}
              >
                <span>مبلغ نهایی قابل پرداخت:</span>
                <span style={{ color: 'var(--brand-neon, #38bdf8)' }}>
                  {finalTotalToman.toLocaleString('fa-IR')} تومان
                </span>
              </div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                  textAlign: 'left',
                  direction: 'ltr',
                }}
              >
                ({(finalTotalToman * 10).toLocaleString('fa-IR')} IRR)
              </div>
            </div>
          </div>
        </Card>

        {/* 2. Organization and Admin Details */}
        <Card title="۲. اطلاعات شرکت و مدیر سیستم">
          <div style={{ display: 'grid', gap: 12 }}>
            <FormField label="نام شرکت یا سازمان" required>
              <Input
                value={organizationName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleCompanyNameChange(e.target.value)
                }
                placeholder="مثال: ساختار سیستم"
                required
              />
            </FormField>

            <FormField label="شناسه آدرس اختصاصی (زیردامنه انگلیسی)" required>
              <div style={{ display: 'flex', alignItems: 'center', direction: 'ltr' }}>
                <Input
                  value={slug}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                  }
                  placeholder="sakhtar"
                  required
                />
                <span style={{ padding: '0 8px', color: '#94a3b8', fontSize: '0.85rem' }}>
                  .cloud.sakhtar.net
                </span>
              </div>
            </FormField>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FormField label="نام مدیر" required>
                <Input
                  value={ownerFirstName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setOwnerFirstName(e.target.value)}
                  placeholder="علی"
                  required
                />
              </FormField>
              <FormField label="نام خانوادگی مدیر" required>
                <Input
                  value={ownerLastName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setOwnerLastName(e.target.value)}
                  placeholder="محمدی"
                  required
                />
              </FormField>
            </div>

            <FormField label="شماره موبایل مدیر (جهت دریافت پیامک ورود و فاکتور)" required>
              <Input
                name="username"
                autoComplete="username tel"
                inputMode="numeric"
                pattern={IRAN_MOBILE_PATTERN}
                maxLength={11}
                dir="ltr"
                value={ownerMobile}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOwnerMobile(e.target.value)}
                placeholder="09121234567"
                type="tel"
                required
              />
            </FormField>

            <FormField label="رمز عبور مدیر" required>
              <Input
                name="password"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="حداقل ۸ کاراکتر"
                type="password"
                required
              />
            </FormField>
          </div>
        </Card>

        {/* 3. Submit Button */}
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={submitting}
          style={{ width: '100%', padding: '14px', fontSize: '1.05rem', fontWeight: 'bold' }}
        >
          {submitting
            ? 'در حال انتقال به درگاه شاپرک...'
            : `پرداخت ${finalTotalToman.toLocaleString('fa-IR')} تومان و تحویل آنی ERP`}
        </Button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 6,
          }}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/start')}
            style={{ fontSize: '0.85rem' }}
          >
            ← بازگشت به صفحه ورود
          </Button>
          <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            درگاه پرداخت امن شاپرک / زرین‌پال
          </span>
        </div>
      </form>
    </AuthSurface>
  );
}
